import { useState } from 'react';
import { authApi } from '../../api/endpoints.js';
import { useNotice } from '../../state/NoticeContext.jsx';
import { Button, Card, Empty, Muted, Select, Spinner, Tag, Text, fmtDate, useAsync } from '../../ui.jsx';

const blank = { name: '', email: '', phone: '', password: '', schoolId: '' };

/**
 * Staff accounts are created here rather than by the person receiving them.
 * A school cannot sign itself up: until someone with a platform admin account
 * provisions it, a school has no way into its own dashboard to publish its
 * dates or answer applicants.
 */
export default function AdminStaff() {
  const notice = useNotice();
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const schools = useAsync(() => authApi.schoolsForStaff(), []);
  const staff = useAsync(() => authApi.listStaff(), []);

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const schoolName = (value) => {
    if (!value) return 'Unassigned';
    if (typeof value === 'string') return value;
    return value.shortName || value.name || 'Unknown school';
  };

  const create = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const data = await authApi.createStaff({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
        schoolId: form.schoolId
      });
      notice.success(`${data.user.name} can now sign in to ${data.school}.`);
      setForm(blank);
      staff.reload();
    } catch (error) {
      notice.fromError(error);
    } finally {
      setBusy(false);
    }
  };

  const rows = staff.data?.staff || [];
  const options = schools.data?.schools || [];
  const tooShort = form.password.length > 0 && form.password.length < 8;

  return (
    <>
      <h2>Staff accounts</h2>
      <Muted>
        Give each school the people who will work its admissions dashboard. Accounts are
        verified from the start, and a staff member only ever sees their own school.
      </Muted>

      <form onSubmit={create}>
        <Card>
          <Text label="Full name" required value={form.name} onChange={set('name')} />
          <div style={{ height: 12 }} />
          <Text label="Email" type="email" required value={form.email} onChange={set('email')} />
          <div style={{ height: 12 }} />
          <Text
            label="Phone"
            value={form.phone}
            onChange={set('phone')}
            hint="Optional. Used for reminders only."
          />
          <div style={{ height: 12 }} />
          <Text
            label="Temporary password"
            type={showPassword ? 'text' : 'password'}
            required
            value={form.password}
            onChange={set('password')}
            error={tooShort ? 'Use at least 8 characters.' : ''}
            hint="Share this once, in person or over a channel you trust. Ask them to change it."
          />
          <button
            type="button"
            className="btn s sm"
            onClick={() => setShowPassword((v) => !v)}
            style={{ marginTop: 8 }}
          >
            {showPassword ? 'Hide password' : 'Show password'}
          </button>
          <div style={{ height: 12 }} />
          <Select
            label="School"
            required
            value={form.schoolId}
            onChange={set('schoolId')}
            hint="This decides which applicants the account can see. It cannot be changed here."
          >
            <option value="">Choose a school</option>
            {options.map((school) => (
              <option key={school._id || school.id} value={school._id || school.id}>
                {school.name}
              </option>
            ))}
          </Select>
          <div style={{ marginTop: 16 }}>
            <Button type="submit" busy={busy} disabled={!form.name || !form.email || !form.password || !form.schoolId || tooShort}>
              Create staff account
            </Button>
          </div>
        </Card>
      </form>

      <Card>
        <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>Accounts</h3>
        {staff.loading ? <Spinner label="Loading staff" /> : null}
        {staff.error ? <p className="no">{staff.error.message}</p> : null}
        {!staff.loading && !staff.error && rows.length === 0 ? (
          <Empty title="No staff accounts yet">
            Create the first one above so a school can start answering applicants.
          </Empty>
        ) : null}
        {rows.length ? (
          <div className="scroll-x">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>School</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((person) => (
                  <tr key={person.id}>
                    <td>{person.name}</td>
                    <td className="mu">{person.email}</td>
                    <td className="mu">{person.phone || 'Not set'}</td>
                    <td>
                      <Tag>{schoolName(person.school)}</Tag>
                    </td>
                    <td className="mu">{fmtDate(person.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>
    </>
  );
}
